import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";

export const Hero = () => {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onReady = () => setVideoReady(true);
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("canplay", onReady);
    v.play().catch(() => {});
    return () => {
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("canplay", onReady);
    };
  }, []);

  return (
    <section data-testid="hero-section" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-mp-black">
      {/* Poster fallback */}
      <img
        src={ASSETS.studioEmpty}
        alt="Estudio Modo Pro"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
          videoReady ? "opacity-0" : "opacity-100"
        }`}
      />
      <video
        ref={videoRef}
        data-testid="hero-video"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
          videoReady ? "opacity-100" : "opacity-0"
        }`}
        src={ASSETS.studioVideo}
        poster={ASSETS.studioEmpty}
        autoPlay muted loop playsInline preload="metadata"
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

      <div className="relative z-10 h-full mx-auto max-w-[1440px] px-6 lg:px-12 flex flex-col justify-end pb-20 md:pb-28">
        <div className="max-w-4xl animate-fade-up">
          <div className="mp-eyebrow mb-6" data-testid="hero-eyebrow">
            <span className="inline-block w-8 h-px bg-mp-orange align-middle mr-3" />
            Modo Pro — Podcast
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display font-bold text-white leading-[0.95] tracking-[-0.02em] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
          >
            Conversaciones reales sobre <em className="not-italic text-mp-orange">dinero</em>, decisiones y oportunidades.
          </h1>
          <p className="mt-6 font-display text-xl md:text-2xl text-white/90 max-w-2xl leading-tight">
            Para los latinos en Estados Unidos.
          </p>
          <p className="mt-8 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl font-body">
            Finanzas, negocios, bienes raíces, emprendimiento, tecnología y decisiones de vida
            explicadas a través de conversaciones reales con quienes las viven, las toman y
            conocen sus consecuencias.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/episodios" data-testid="hero-cta-primary" className="mp-btn-primary">
              Ver Episodios <ArrowRight size={16} />
            </Link>
            <Link to="/elda-medina" data-testid="hero-cta-secondary" className="mp-btn-ghost">
              Conoce Modo Pro
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom marker */}
      <div className="absolute bottom-6 right-6 md:right-12 z-10 hidden md:flex items-center gap-3 text-white/60">
        <div className="w-10 h-px bg-white/40" />
        <span className="font-display text-[10px] tracking-[0.32em] uppercase">South Florida · Español</span>
      </div>
    </section>
  );
};

export default Hero;
